import moment from 'moment';
import { getGstData } from '@app/components/Cart';
import { generateUniqueCode } from '@src/utilities/common';
import type { OrderServiceType } from '@constants/orderServiceType';
import type {
    TableCart,
    CartItem,
    BillingSummary,
    GstBreakdown,
    RestaurantDetails,
    UserData,
    CustomerDetails,
} from '../types';

/**
 * Compute the full billing summary for the current table cart.
 */
export function computeBillingSummary(
    tableCart: TableCart,
    isGstBill: boolean,
    gstRate: number,
    serviceRate: number,
    discount: number
): BillingSummary {
    let totalCost = 0;
    Object.values(tableCart).forEach((val) => {
        const item = val as CartItem | null;
        if (item?.selectedVariants?.value && item?.selected) {
            totalCost += item.selectedVariants.value * item.selected;
        }
    });

    const gstBreakdown: GstBreakdown = getGstData(tableCart, isGstBill, gstRate, totalCost);
    const effectiveServiceRate = isGstBill ? serviceRate : 0;
    const serviceBill = Number((totalCost * effectiveServiceRate / 100).toFixed(2));
    const grandTotal = Math.round(
        totalCost + Math.ceil(gstBreakdown.totalGSTCost) + serviceBill - (discount > 0 ? discount : 0)
    );

    return {
        totalCost,
        discount,
        effectiveServiceRate,
        serviceBill,
        gstBreakdown,
        grandTotal,
        isGstBill,
    };
}

/**
 * Build the order payload expected by `submitOrder()`.
 */
export function buildOrderDetails(params: {
    tableCart: TableCart;
    activeTable: string;
    billing: BillingSummary;
    user: UserData;
    roles: Record<string, { role: string }>;
    selectedRestId: string;
    selectedRestaurant: RestaurantDetails;
    restaurantId: string;
    customer: CustomerDetails;
    serviceType: OrderServiceType;
}) {
    const { tableCart, activeTable, billing, user, roles, selectedRestId, selectedRestaurant, restaurantId, customer, serviceType } = params;

    return {
        uoId: generateUniqueCode(6),
        cart: tableCart,
        tableNumber: activeTable,
        selectedPayment: 'cash',
        user: {
            email: user?.email ?? 'NA',
            name: user?.name ?? 'NA',
            phone: user?.phone ?? 'NA',
            phoneNumber: user?.phoneNumber ?? 'NA',
            role: roles[selectedRestId]?.role ?? 'NA',
            uid: user?.uid ?? 'NA',
        },
        userId: user?.phoneNumber,
        restaurant: {
            address: selectedRestaurant?.address,
            docId: selectedRestaurant?.docId,
            foodType: selectedRestaurant?.foodType,
            gstIn: selectedRestaurant?.gstIn,
            gstRate: billing.isGstBill ? selectedRestaurant?.gstRate : 0,
            serviceRate: billing.isGstBill ? selectedRestaurant?.serviceRate : 0,
            location: selectedRestaurant?.location,
            name: selectedRestaurant?.name,
            ownerId: selectedRestaurant?.ownerId,
            ownerName: selectedRestaurant?.ownerName,
            ownerPhone: selectedRestaurant?.ownerPhone,
        },
        restaurantId,
        isGstBill: billing.isGstBill,
        customerPhone: customer.customerPhone || '',
        customerName: customer.customerName || '',
        gstRate: billing.isGstBill ? +(selectedRestaurant?.gstRate || 0) : 0,
        serviceRate: billing.effectiveServiceRate,
        discount: billing.discount,
        itemsCost: billing.totalCost || '',
        orderStatus: { label: 'Pending', value: 'pending' },
        gstBill: billing.gstBreakdown.totalGSTCost || '',
        serviceBill: billing.serviceBill,
        gstData: billing.gstBreakdown.gstData || {},
        billingAddress: customer.billingAddress || '',
        billingName: customer.billingName || '',
        gstNumber: customer.gstNumber || '',
        totalCost: billing.grandTotal,
        createdAt: moment().utc().valueOf(),
        serviceType,
    };
}
